import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { getStoredUser } from '@/hooks/useAuth';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import { useOwnerDashboard } from '@/hooks/useOwnerDashboard';
import { OwnerStation, formatMoney, formatMoneyShort } from '@/mocks/ownerDashboard';

const THEME_KEY = 'helios:theme';
const PREV_KEY = 'hlxPrevTheme';
// Restore the user's pre-Lab theme in localStorage (used on exit / unmount).
function restoreTheme() {
  try {
    const prev = sessionStorage.getItem(PREV_KEY);
    if (prev !== null) { localStorage.setItem(THEME_KEY, prev); sessionStorage.removeItem(PREV_KEY); }
  } catch { /* noop */ }
}

/**
 * EXPERIMENTAL "trading-terminal" owner dashboard (crypto-exchange aesthetic).
 * Dark glass UI, neon gradients, live pulse. Reuses the live /owner/dashboard
 * data. Lives at /dashboard/owner/lab; the classic page stays untouched.
 */

const NEON = {
  cyan: '#22D3EE',
  violet: '#A855F7',
  green: '#34D399',
  red: '#FB5E7E',
  amber: '#FBBF24',
  grey: '#64748B',
  blue: '#60A5FA',
  pink: '#F472B6',
};
const PALETTE = [NEON.cyan, NEON.violet, NEON.green, NEON.amber, NEON.blue, NEON.pink];

const statusNeon = (s: string) =>
  s === 'green' ? NEON.green : s === 'amber' ? NEON.amber : s === 'red' ? NEON.red : NEON.grey;
const statusWord = (s: string) =>
  s === 'green' ? 'ONLINE' : s === 'amber' ? 'LIMITED' : s === 'red' ? 'HALTED' : s === 'pre_launch' ? 'PENDING' : '—';

const CSS = `
@keyframes hlxPulse { 0%,100% { opacity:1; transform:scale(1);} 50% { opacity:.35; transform:scale(.7);} }
@keyframes hlxGlow { 0%,100% { opacity:.5;} 50% { opacity:1;} }
@keyframes hlxFloat { 0%,100% { transform:translateY(0);} 50% { transform:translateY(-6px);} }
@keyframes hlxSweep { 0% { background-position:-200% 0;} 100% { background-position:200% 0;} }
.hlx-row { transition: background .18s, box-shadow .18s, transform .18s; }
.hlx-row:hover { background: rgba(34,211,238,.06) !important; box-shadow: inset 3px 0 0 ${NEON.cyan}; transform: translateX(2px); }
.hlx-card { transition: transform .2s, box-shadow .2s, border-color .2s; }
.hlx-card:hover { transform: translateY(-3px); border-color: rgba(34,211,238,.4) !important; box-shadow: 0 8px 40px rgba(34,211,238,.12); }
.hlx-grad-text { background:linear-gradient(110deg,#22D3EE,#A855F7 60%,#F472B6); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; }

/* LAB chrome — turn the whole shell dark & on-theme, a tone lighter than the
   central block, with a neon-lit topbar. Scoped to body.hlx-lab so it only
   applies while the Terminal Lab page is mounted. */
body.hlx-lab main {
  background:
    radial-gradient(1000px 620px at 14% -12%, rgba(34,211,238,.07), transparent 55%),
    radial-gradient(900px 620px at 100% -4%, rgba(168,85,247,.08), transparent 55%),
    linear-gradient(180deg,#0F1116,#0B0D11) !important;
}
body.hlx-lab header {
  background: linear-gradient(180deg,#15181E,#0F1116) !important;
  border-bottom: 1px solid rgba(34,211,238,.12) !important;
  box-shadow: 0 10px 34px rgba(168,85,247,.06) !important;
}
body.hlx-lab header::after {
  content:''; position:absolute; left:0; right:0; bottom:0; height:1px;
  background:linear-gradient(90deg,transparent,#22D3EE 28%,#A855F7 72%,transparent);
  opacity:.6; pointer-events:none;
}
`;

function MiniBar({ pct, color }: { pct: number; color: string }) {
  return (
    <div style={{ width: 64, height: 5, borderRadius: 99, background: 'rgba(255,255,255,.07)', overflow: 'hidden' }}>
      <div style={{ width: `${Math.max(2, Math.min(100, pct))}%`, height: '100%', borderRadius: 99, background: color, boxShadow: `0 0 8px ${color}` }} />
    </div>
  );
}

export default function OwnerDashboardLab() {
  const { stations, incidents, kpis, loading } = useOwnerDashboard();
  const navigate = useNavigate();
  const { setSelectedAsset } = useSelectedAsset();
  const user = getStoredUser();

  // Terminal Lab is a fully-dark experience. The dark theme is set in
  // localStorage BEFORE the full-page navigation into Lab (see the entry button),
  // so the shell (sidebar/topbar/SES selector/background) renders dark from
  // ThemeProvider init — no in-render setState, no broken route transitions.
  // Here we only add the <body> tag for the scoped neon-chrome CSS, and make
  // sure the original theme is restored in storage if the user leaves Lab.
  useEffect(() => {
    document.body.classList.add('hlx-lab');
    // Direct-URL access (not via the entry button): ensure dark, then reload once
    // so the shell renders dark from ThemeProvider init. Guarded — no loop.
    try {
      if (localStorage.getItem(THEME_KEY) !== 'dark') {
        if (sessionStorage.getItem(PREV_KEY) === null) {
          sessionStorage.setItem(PREV_KEY, localStorage.getItem(THEME_KEY) || 'light');
        }
        localStorage.setItem(THEME_KEY, 'dark');
        window.location.reload();
        return;
      }
    } catch { /* noop */ }
    return () => {
      document.body.classList.remove('hlx-lab');
      restoreTheme();
    };
  }, []);

  const exitToClassic = () => { restoreTheme(); window.location.assign('/dashboard/owner'); };

  const open = (s: OwnerStation) => {
    setSelectedAsset({
      id: s.id, assetName: s.name, installedCapacityMw: s.installedMw,
      region: s.region, operationalStatus: s.status,
    });
    navigate(`/assets/${s.id}`);
  };

  const totalRev = stations.reduce((a, s) => a + (s.todayRevenueUah || 0), 0);
  const ranked = [...stations].sort((a, b) => (b.todayRevenueUah || 0) - (a.todayRevenueUah || 0));
  const colorOf: Record<string, string> = {};
  ranked.forEach((s, i) => { colorOf[s.id] = PALETTE[i % PALETTE.length]; });

  const card = {
    background: 'linear-gradient(160deg, rgba(255,255,255,.05), rgba(255,255,255,.015))',
    border: '1px solid rgba(255,255,255,.08)',
    borderRadius: 16,
    backdropFilter: 'blur(8px)',
  } as const;
  const mono = { fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace' } as const;
  const dim = '#7C8499';
  const faint = '#4A5163';

  const dash = loading ? '···' : '—';
  const kpiTiles = [
    { k: 'ДОХІД / МІСЯЦЬ', v: kpis ? formatMoney(kpis.earnedMonthUah) : dash, c: NEON.cyan, sub: 'гарантовано · РДН −3%' },
    { k: 'ДОХІД / СЬОГОДНІ', v: kpis ? formatMoney(kpis.earnedTodayUah) : dash, c: NEON.green, sub: kpis ? `план: ${formatMoneyShort(kpis.forecastTodayRevenueUah)} грн` : '' },
    { k: 'ПРОГНОЗ / ДЕНЬ', v: kpis ? `${kpis.forecastTodayMwh ?? 0}` : dash, unit: 'МВт·год', c: NEON.violet, sub: 'очікуване вироблення' },
    { k: 'ПОТУЖНІСТЬ ЗАРАЗ', v: kpis ? `${kpis.factNowMw ?? 0}` : dash, unit: 'МВт', c: NEON.amber, sub: 'live', live: true },
  ];

  return (
    <DashboardLayout breadcrumb={['Дашборд', 'Lab']}>
      <style>{CSS}</style>
      <div
        style={{
          position: 'relative',
          margin: -4,
          padding: 20,
          borderRadius: 20,
          color: '#E6E9F0',
          background:
            'radial-gradient(900px 500px at 12% -10%, rgba(34,211,238,.10), transparent 55%),' +
            'radial-gradient(800px 500px at 100% 0%, rgba(168,85,247,.12), transparent 55%),' +
            'linear-gradient(180deg,#0A0C12,#070809)',
          overflow: 'hidden',
        }}
      >
        {/* subtle grid overlay */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', opacity: .35,
          backgroundImage: 'linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)',
          backgroundSize: '38px 38px',
        }} />

        {/* ── TICKER / HERO ───────────────────────────── */}
        <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
          <div>
            <div style={{ ...mono, fontSize: 10, letterSpacing: 3, color: dim, marginBottom: 6 }}>
              HELIOS · PORTFOLIO TERMINAL{user ? ` · ${user.displayName?.toUpperCase()}` : ''}
            </div>
            <div className="hlx-grad-text" style={{ ...mono, fontSize: 46, fontWeight: 800, lineHeight: 1, letterSpacing: -1 }}>
              {kpis ? formatMoney(kpis.earnedMonthUah) : dash}
            </div>
            <div style={{ fontSize: 12, color: dim, marginTop: 6 }}>дохід з початку місяця · {stations.length || 6} активів</div>
          </div>

          <div style={{ display: 'flex', gap: 22, alignItems: 'center', ...mono }}>
            <Stat label="СЬОГОДНІ" value={kpis ? formatMoneyShort(kpis.earnedTodayUah) + ' ₴' : dash} color={NEON.green} />
            <Divider />
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: faint }}>LIVE · МВт</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'flex-end' }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: NEON.green, boxShadow: `0 0 10px ${NEON.green}`, animation: 'hlxPulse 1.6s infinite' }} />
                <span style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>{kpis ? (kpis.factNowMw ?? 0) : dash}</span>
              </div>
            </div>
            <Divider />
            <Stat label="АКТИВНІ" value={kpis ? `${kpis.workingCount}/${kpis.totalCount}` : dash} color={NEON.cyan} />
          </div>
        </div>

        {/* ── KPI GLASS CARDS ─────────────────────────── */}
        <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 14, marginBottom: 18 }}>
          {kpiTiles.map((t) => (
            <div key={t.k} className="hlx-card" style={{ ...card, padding: 16 }}>
              <div style={{ ...mono, fontSize: 10, letterSpacing: 2, color: dim, marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
                <span>{t.k}</span>
                {t.live && <span style={{ width: 6, height: 6, borderRadius: 99, background: NEON.amber, boxShadow: `0 0 8px ${NEON.amber}`, animation: 'hlxPulse 1.6s infinite' }} />}
              </div>
              <div style={{ ...mono, fontSize: 26, fontWeight: 800, color: '#fff', letterSpacing: -.5 }}>
                {t.v}{t.unit && <span style={{ fontSize: 12, color: dim, marginLeft: 5 }}>{t.unit}</span>}
              </div>
              <div style={{ height: 3, marginTop: 12, borderRadius: 99, background: `linear-gradient(90deg, ${t.c}, transparent)`, boxShadow: `0 0 12px ${t.c}66` }} />
              <div style={{ fontSize: 11, color: dim, marginTop: 8 }}>{t.sub}</div>
            </div>
          ))}
        </div>

        <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'minmax(0,1.55fr) minmax(0,1fr)', gap: 14 }}>
          {/* ── ASSETS "MARKET" TABLE ─────────────────── */}
          <div className="hlx-card" style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
              <div style={{ ...mono, fontSize: 12, letterSpacing: 1, color: '#fff', fontWeight: 700 }}>СТАНЦІЇ · МАРКЕТ</div>
              <div style={{ ...mono, fontSize: 10, color: faint }}>натисніть рядок → сторінка активу</div>
            </div>
            {/* header */}
            <div style={{ ...mono, display: 'grid', gridTemplateColumns: '1.6fr .9fr 1fr 1.1fr', gap: 8, padding: '8px 16px', fontSize: 10, letterSpacing: 1, color: faint, borderBottom: '1px solid rgba(255,255,255,.05)' }}>
              <span>СТАНЦІЯ</span><span>СТАТУС</span><span style={{ textAlign: 'right' }}>ЕФ. ПРОГНОЗУ</span><span style={{ textAlign: 'right' }}>ДОХІД / ДЕНЬ</span>
            </div>
            {ranked.map((s) => {
              const c = statusNeon(s.status);
              const pre = s.status === 'pre_launch';
              return (
                <button
                  key={s.id}
                  onClick={() => open(s)}
                  className="hlx-row"
                  style={{ all: 'unset', cursor: 'pointer', display: 'grid', gridTemplateColumns: '1.6fr .9fr 1fr 1.1fr', gap: 8, alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,.04)', width: 'calc(100% - 32px)' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 99, background: c, boxShadow: `0 0 8px ${c}`, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#EDF0F6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name.replace('СЕС ', '')}</div>
                      <div style={{ ...mono, fontSize: 10, color: faint }}>{s.installedMw} МВт{s.statusNote ? ` · ${s.statusNote}` : ''}</div>
                    </div>
                  </div>
                  <span style={{ ...mono, justifySelf: 'start', fontSize: 10, fontWeight: 700, letterSpacing: 1, color: c, padding: '3px 8px', borderRadius: 6, background: `${c}1A`, border: `1px solid ${c}40` }}>
                    {statusWord(s.status)}
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <span style={{ ...mono, fontSize: 14, fontWeight: 700, color: pre || s.percentOfPotential == null ? faint : c }}>
                      {pre || s.percentOfPotential == null ? '—' : `${s.percentOfPotential}%`}
                    </span>
                    {!pre && s.percentOfPotential != null && <MiniBar pct={s.percentOfPotential} color={c} />}
                  </div>
                  <span style={{ ...mono, textAlign: 'right', fontSize: 14, fontWeight: 700, color: pre ? faint : '#fff' }}>
                    {pre ? '—' : `${formatMoneyShort(s.todayRevenueUah)} ₴`}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ── RIGHT COLUMN: allocation + alerts ─────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
            {/* Allocation */}
            <div className="hlx-card" style={{ ...card, padding: 16 }}>
              <div style={{ ...mono, fontSize: 12, letterSpacing: 1, color: '#fff', fontWeight: 700, marginBottom: 14 }}>РОЗПОДІЛ ДОХОДУ</div>
              <div style={{ display: 'flex', height: 14, borderRadius: 99, overflow: 'hidden', background: 'rgba(255,255,255,.05)' }}>
                {ranked.filter((s) => (s.todayRevenueUah || 0) > 0).map((s) => (
                  <div key={s.id} title={`${s.name}: ${formatMoneyShort(s.todayRevenueUah)} ₴`}
                    style={{ width: `${totalRev ? (s.todayRevenueUah / totalRev) * 100 : 0}%`, background: colorOf[s.id], boxShadow: `0 0 10px ${colorOf[s.id]}88` }} />
                ))}
              </div>
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 9 }}>
                {ranked.map((s) => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: (s.todayRevenueUah || 0) > 0 ? colorOf[s.id] : faint, flexShrink: 0 }} />
                    <span style={{ color: '#C5CBD8', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name.replace('СЕС ', '')}</span>
                    <span style={{ ...mono, color: '#fff', fontWeight: 600 }}>
                      {(s.todayRevenueUah || 0) > 0 ? `${Math.round((s.todayRevenueUah / totalRev) * 100)}%` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Alerts */}
            <div className="hlx-card" style={{ ...card, padding: 16, flex: 1 }}>
              <div style={{ ...mono, fontSize: 12, letterSpacing: 1, color: '#fff', fontWeight: 700, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
                <span>ІНЦИДЕНТИ</span>
                <span style={{ color: incidents.length ? NEON.amber : NEON.green }}>{incidents.length || 0}</span>
              </div>
              {incidents.length === 0 && (
                <div style={{ fontSize: 12, color: NEON.green, padding: '10px 0' }}>✓ Усі системи в нормі</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {incidents.map((inc, i) => {
                  const c = inc.severity === 'red' ? NEON.red : NEON.amber;
                  return (
                    <div key={i} style={{ padding: '9px 11px', borderRadius: 10, background: 'rgba(255,255,255,.03)', borderLeft: `3px solid ${c}`, boxShadow: `inset 0 0 18px ${c}10` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                        <span style={{ width: 7, height: 7, borderRadius: 99, background: c, boxShadow: `0 0 7px ${c}`, animation: 'hlxGlow 2s infinite' }} />
                        <span style={{ ...mono, fontSize: 12, fontWeight: 700, color: '#EDF0F6' }}>{inc.station}</span>
                      </div>
                      <div style={{ fontSize: 11, color: dim, lineHeight: 1.4 }}>{inc.description}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div style={{ position: 'relative', marginTop: 16, ...mono, fontSize: 10, color: faint, display: 'flex', justifyContent: 'space-between' }}>
          <span>HELIOS AGGREGATE · {loading ? 'SYNCING…' : 'LIVE'}</span>
          <button onClick={exitToClassic} style={{ all: 'unset', cursor: 'pointer', color: NEON.cyan }}>← класична версія</button>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 10, letterSpacing: 2, color: '#4A5163' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
function Divider() {
  return <div style={{ width: 1, height: 30, background: 'rgba(255,255,255,.1)' }} />;
}
